import { HttpClient, HttpHeaders, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {combineLatest, Observable, of, zip} from 'rxjs';
import {map, share, switchMap, catchError, filter, take, concatMap} from 'rxjs/operators';
import { BusinessNavSessionService } from '../session.service';
import {
  ContactUsRequest,
  GetLanguageResponse,
  GetLocationsRequest,
  GetLocationsResponse,
  GetNavigationDataRequest,
  GetNavigationDataResponse,
  GetPinsRequest,
  GetPinsResponse,
  SetLanguageRequest,
  SetPinsRequest,
  SetDefaultLocationRequest,
  ListElevatedLocationsRequest,
  ListElevatedLocationsResponse,
  GetSalesInfoRequest,
  GetSalesInfoResponse
} from './api';
import {
  ContactUsRequestInterface,
  GetLanguageResponseInterface,
  GetLocationsRequestInterface,
  GetLocationsResponseInterface,
  GetNavigationDataRequestInterface,
  GetNavigationDataResponseInterface,
  GetPinsRequestInterface,
  GetPinsResponseInterface,
  SetLanguageRequestInterface,
  SetPinsRequestInterface,
  SetDefaultLocationRequestInterface,
  ListElevatedLocationsRequestInterface,
  ListElevatedLocationsResponseInterface,
  GetSalesInfoRequestInterface,
  GetSalesInfoResponseInterface
} from './api.interface';
import { HostService } from './host.service';

@Injectable({providedIn: 'root'})
export class AtlasApiService {

  constructor(private http: HttpClient, private hostService: HostService, private sessionService: BusinessNavSessionService) {
  }

  private apiOptions(session: string): { headers: HttpHeaders, withCredentials: boolean } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session}`
      }),
      withCredentials: false
    };
  }

  getNavigationData(r: GetNavigationDataRequest | GetNavigationDataRequestInterface): Observable<GetNavigationDataResponse> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as GetNavigationDataRequest).toApiJson) ? (r as GetNavigationDataRequest) : new GetNavigationDataRequest(r);
        return this.http.post<GetNavigationDataResponseInterface>(hostWithScheme + '/atlas.v1.Atlas/GetNavigationData', request.toApiJson(), this.apiOptions(session));
      }),
      catchError((err: HttpErrorResponse, _: Observable<GetNavigationDataResponse>) => {
        if (err.status === 401) {
          console.log('Unauthorized; Unable to retrieve data');
        }
        if (err.status === 403) {
          console.log('Forbidden; Unable to retrieve data');
        }
        console.log('Failed to fetch Business Nav data, attempting to use cache...');
        return of({});
      }),
      map(resp => GetNavigationDataResponse.fromProto(resp)),
      share()
    );
  }

  getSalesInfo(r: GetSalesInfoRequest | GetSalesInfoRequestInterface): Observable<GetSalesInfoResponse> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as GetSalesInfoRequest).toApiJson) ? (r as GetSalesInfoRequest) : new GetSalesInfoRequest(r);
        return this.http.post<GetSalesInfoResponseInterface>(hostWithScheme + '/atlas.v1.Atlas/GetSalesInfo', request.toApiJson(), this.apiOptions(session));
      }),
      catchError((err: HttpErrorResponse, _: Observable<GetSalesInfoResponse>) => {
        if (err.status === 401) {
          console.log('Unauthorized; Unable to retrieve data');
        }
        if (err.status === 403) {
          console.log('Forbidden; Unable to retrieve data');
        }
        console.log('Failed to fetch Business Nav data, attempting to use cache...');
        return of({});
      }),
      map(resp => GetSalesInfoResponse.fromProto(resp)),
      share()
    );
}

  setPins(r: SetPinsRequest | SetPinsRequestInterface): Observable<boolean> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      filter(([session, hostWithScheme]: [string, string]) => !!session),
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as SetPinsRequest).toApiJson) ? (r as SetPinsRequest) : new SetPinsRequest(r);
        return this.http.post<null>(hostWithScheme + '/atlas.v1.Pins/SetPins', request.toApiJson(), this.apiOptions(session));
      }),
      map(resp => true),
      share()
    );
  }

  getPins(r: GetPinsRequest | GetPinsRequestInterface): Observable<GetPinsResponse> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      filter(([session, hostWithScheme]: [string, string]) => !!session),
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as GetPinsRequest).toApiJson) ? (r as GetPinsRequest) : new GetPinsRequest(r);
        return this.http.post<GetPinsResponseInterface>(hostWithScheme + '/atlas.v1.Pins/GetPins', request.toApiJson(), this.apiOptions(session));
      }),
      map(resp => GetPinsResponse.fromProto(resp)),
      share()
    );
  }

  getTranslationFile(lang: string): Observable<JSON> {
    return this.hostService.hostWithScheme$.pipe(
      switchMap((hostWithScheme: string) => {
        return this.http.get(hostWithScheme + `/assets/${lang}.json`)
          .pipe(
            map((response: JSON) => response),
            share(),
          );
      })
    );
  }

  getLocations(r: GetLocationsRequest | GetLocationsRequestInterface): Observable<GetLocationsResponse> {
    return zip(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      take(1),
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as GetLocationsRequest).toApiJson) ? (r as GetLocationsRequest) : new GetLocationsRequest(r);
        return this.http.post<GetLocationsResponseInterface>(hostWithScheme + '/atlas.v1.Atlas/GetLocations', request.toApiJson(), this.apiOptions(session));
      }),
      map(resp => GetLocationsResponse.fromProto(resp)),
      share()
    );
  }

  setLanguage(r: SetLanguageRequest | SetLanguageRequestInterface): Observable<boolean> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as SetLanguageRequest).toApiJson) ? (r as SetLanguageRequest) : new SetLanguageRequest(r);
        return this.http.post<null>(hostWithScheme + '/atlas.v1.Languages/SetLanguage', request.toApiJson(), this.apiOptions(session));
      }),
      map(() => true),
      share()
    );
  }

  getLanguage(): Observable<GetLanguageResponse> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        return this.http.post<GetLanguageResponseInterface>(hostWithScheme + '/atlas.v1.Languages/GetLanguage', {}, this.apiOptions(session));
      }),
      map(resp => GetLanguageResponse.fromProto(resp)),
      share()
    );
  }

  contactUs(r: ContactUsRequest | ContactUsRequestInterface): Observable<boolean> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as ContactUsRequest).toApiJson) ? (r as ContactUsRequest) : new ContactUsRequest(r);
        return this.http.post<null>(hostWithScheme + '/atlas.v1.Atlas/ContactUs', request.toApiJson(), this.apiOptions(session));
      }),
      map(() => true),
    );
  }

  setDefaultLocation(r: SetDefaultLocationRequest | SetDefaultLocationRequestInterface): Observable<HttpResponse<null>> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as SetDefaultLocationRequest).toApiJson) ? (r as SetDefaultLocationRequest) : new SetDefaultLocationRequest(r);
        return this.http.post<null>(hostWithScheme + '/atlas.v1.Atlas/SetDefaultLocation', request.toApiJson(), this.apiOptions(session));
      })
    );
  }

  listElevatedLocations(r: ListElevatedLocationsRequest | ListElevatedLocationsRequestInterface): Observable<ListElevatedLocationsResponse> {
    return combineLatest(this.sessionService.sessionId$, this.hostService.hostWithScheme$).pipe(
      switchMap(([session, hostWithScheme]: [string, string]) => {
        const request = ((r as ListElevatedLocationsRequest).toApiJson) ? (r as ListElevatedLocationsRequest) : new ListElevatedLocationsRequest(r);
        return this.http.post<ListElevatedLocationsResponseInterface>(hostWithScheme + '/atlas.v1.Atlas/ListElevatedLocations', request.toApiJson(), this.apiOptions(session));
      }),
      map(resp => ListElevatedLocationsResponse.fromProto(resp)),
      share()
    );
  }
}
